import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  HeadContent,
  Scripts,
} from "@tanstack/react-router";

import appCss from "../styles.css?url";
import { CookieBanner } from "@/components/CookieBanner";
import { SentryBoundary } from "@/components/SentryBoundary";
import { initSentry } from "@/lib/sentry";

initSentry();

const OG_IMAGE = "https://whoknew.guide/whoknew-og-image.png";

function NotFoundComponent() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">Page not found</h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you're looking for doesn't exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to="/"
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Go home
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn't load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
          >
            Try again
          </button>
          <a
            href="/"
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1" },
      { title: "WHO KNEW" },
      { name: "description", content: "Plain-English courses on the topics most business people quietly wish they understood. First one out: reading a set of UK company accounts, in three hours, without the jargon." },
      { property: "og:site_name", content: "WHO KNEW" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: OG_IMAGE },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "WHO KNEW - Field guides for people who've been confidently nodding along" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: OG_IMAGE },
      { property: "og:title", content: "WHO KNEW - Field guides for people who've been confidently nodding along" },
      { name: "twitter:title", content: "WHO KNEW - Field guides for people who've been confidently nodding along" },
      { property: "og:description", content: "Plain-English courses on the topics most business people quietly wish they understood. First one out: reading a set of UK company accounts, in three hours, without the jargon." },
      { name: "twitter:description", content: "Plain-English courses on the topics most business people quietly wish they understood. First one out: reading a set of UK company accounts, in three hours, without the jargon." },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      { rel: "preload", as: "font", type: "font/woff2", href: "/fonts/cormorant.woff2", crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: "/fonts/cormorant-italic.woff2", crossOrigin: "anonymous" },
      { rel: "preload", as: "font", type: "font/woff2", href: "/fonts/jetbrains-mono.woff2", crossOrigin: "anonymous" },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <HeadContent />
        <link rel="icon" type="image/svg+xml" href="/favicon.svg?v=dot" />
        <link rel="icon" type="image/png" sizes="32x32" href="/favicon-32x32.png?v=dot" />
        <link rel="icon" type="image/png" sizes="16x16" href="/favicon-16x16.png?v=dot" />
        <link rel="shortcut icon" href="/favicon.ico?v=dot" />
        <link rel="alternate icon" href="/favicon.ico?v=dot" />
        <link rel="apple-touch-icon" href="/apple-touch-icon.png?v=dot" />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();

  return (
    <QueryClientProvider client={queryClient}>
      <SentryBoundary>
        <Outlet />
      </SentryBoundary>
      <CookieBanner />
    </QueryClientProvider>
  );
}
